/**
 * @file dynamic-domain.ts
 * @author Rapoo Device SDK Team
 * @date 2026-07-21
 * @description Telink 键盘动态灯效领域描述
 */

import {
  LightingDomainDescriptor,
  LightingDomainType,
  LightingFeatureFlag,
} from '../../../../../types/lightingDomain'
import { getAddress } from '../../../../../utils/common'
import { ADDRESS_TELINK } from '../../../protocols/address'
import { sanitizeLightingLevel } from '../../../../shared/feature-impls/keyboard-lighting/lighting'

export const dynamicDomain: LightingDomainDescriptor = {
  type: LightingDomainType.Dynamic,
  configSize: 8,
  supportedFeatures:
    LightingFeatureFlag.Brightness |
    LightingFeatureFlag.Speed |
    LightingFeatureFlag.SingleColor |
    LightingFeatureFlag.Colorful,

  addressResolver: {
    getBaseAddr: (configIndex, itemIndex) => {
      return getAddress(ADDRESS_TELINK.DYNAMIC_LIGHT_BRIGHTNESS, configIndex, itemIndex)
    },
    getFieldAddr: (field, configIndex, itemIndex) => {
      // 必须查 Telink 自己的字段地址表：动态参数表共 16 项且地址不连续
      // （模式 11 起跳到 +0x00BC），既不能按「基地址 + 固定步长」外推，
      // 也不能借用其他芯片的地址表。
      const fieldAddressMap = {
        brightness: ADDRESS_TELINK.DYNAMIC_LIGHT_BRIGHTNESS,
        speed: ADDRESS_TELINK.DYNAMIC_LIGHT_SPEED,
        isColorful: ADDRESS_TELINK.DYNAMIC_LIGHT_COLOR_TYPE,
      } as const
      const table =
        fieldAddressMap[field as keyof typeof fieldAddressMap] ??
        ADDRESS_TELINK.DYNAMIC_LIGHT_BRIGHTNESS
      return getAddress(table, configIndex, itemIndex)
    },
  },

  parser: {
    parse: (buffer, index) => ({
      index,
      domainType: LightingDomainType.Dynamic,
      brightness: sanitizeLightingLevel(buffer[0]),
      speed: sanitizeLightingLevel(buffer[1]),
      isColorful: buffer[2] === 0,
      rgb: { r: buffer[4], g: buffer[5], b: buffer[6] },
      buffer,
    }),
    serialize: (config) => {
      const buf = new Uint8Array(8)
      buf[0] = sanitizeLightingLevel(config.brightness)
      buf[1] = sanitizeLightingLevel(config.speed)
      buf[2] = config.isColorful ? 0 : 1
      buf[3] = 0
      buf[4] = config.rgb?.r ?? 255
      buf[5] = config.rgb?.g ?? 255
      buf[6] = config.rgb?.b ?? 255
      buf[7] = 0
      return buf
    },
  },

  normalizeIndex: (index) => Math.max(0, index - 100),

  cacheTags: (index, configIndex) => [`light:dynamic:${index}`, `cfg:${configIndex}`],
}
